/**
 * BrandingContext — Scenario OS (Tier 2) learner-app theming.
 *
 * On bootstrap (and whenever the active client_id changes — e.g. after
 * login adds ``learner.client_id`` to the profile) we hit
 * ``GET /scenario-os/branding`` or ``/branding/{client_id}`` and apply
 * the returned palette as CSS custom properties on ``:root`` so every
 * component using ``var(--accent)`` / ``var(--bg-primary)`` / etc.
 * re-themes without a redeploy.
 *
 * Resolution order for the active client_id:
 *   1. ``REACT_APP_TENANT_ID`` — build-time override for partner pilots
 *      whose entire SPA bundle is single-tenant (Feenix Partner).
 *   2. ``learner.client_id`` from AuthContext — set by the backend's
 *      ``/learner/profile`` once a learner is associated to a tenant.
 *   3. Fall through to the default branding (``scenario_os``).
 *
 * The backend route is fail-soft (returns hard-coded defaults on
 * config_store miss), so a 404 / 500 here just leaves the in-bundle
 * dark theme in place — never breaks rendering.
 *
 * Counterpart backend: ``core/scenario/api/branding_routes.py``.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { brandingAPI } from '../services/api';

const BrandingContext = createContext(null);

/**
 * Mapping from branding payload keys → CSS custom property names. We
 * keep this explicit so a backend payload change can't silently
 * inject arbitrary CSS variables onto :root.
 *
 * Backend payload shape (see branding_routes.py):
 *   {
 *     "client_id": "scenario_os",
 *     "display_name": "Scenario OS",
 *     "logo_url": "...",
 *     "favicon_url": "...",
 *     "theme": {
 *       "primary": "#4fc3f7",
 *       "accent": "#4fc3f7",
 *       "bg_primary": "#0f0f23",
 *       "bg_secondary": "#1a1a2e",
 *       "bg_card": "#16213e",
 *       "text_primary": "#e0e0e0",
 *       "text_secondary": "#a0a0b0",
 *       "success": "#66bb6a",
 *       "error": "#ef5350",
 *       "warning": "#ffa726",
 *     },
 *   }
 */
/**
 * Maps Scenario OS branding API payload keys → canonical --fg-* CSS custom
 * property names (defined in core/ui/shared/theme/tokens.css).
 *
 * Old non-canonical names (e.g. --accent, --bg-primary) are no longer used;
 * tokens.css provides backward-compat aliases from the old names to --fg-*
 * so any remaining CSS that reads var(--accent) or var(--bg-primary) still works.
 */
const THEME_VAR_MAP = {
  primary:        '--fg-accent',
  accent:         '--fg-accent',
  accent_hover:   '--fg-accent-hover',
  bg_primary:     '--fg-bg-app',
  bg_secondary:   '--fg-bg-surface',
  bg_card:        '--fg-bg-raised',
  text_primary:   '--fg-text-primary',
  text_color:     '--fg-text-primary',
  text_secondary: '--fg-text-secondary',
  success:        '--fg-success',
  error:          '--fg-error',
  warning:        '--fg-warning',
  info:           '--fg-info',
};

const DEFAULT_BRANDING = {
  client_id: 'scenario_os',
  display_name: 'TeachIQ',
  logo_url: null,
  favicon_url: null,
  theme: {},
};

function applyThemeVars(theme) {
  if (!theme || typeof theme !== 'object') return;
  const root = document.documentElement;
  Object.entries(theme).forEach(([k, v]) => {
    const varName = THEME_VAR_MAP[k];
    if (!varName) return;
    if (typeof v === 'string' && v.trim()) {
      root.style.setProperty(varName, v.trim());
    }
  });
}

function applyFavicon(url) {
  if (!url) return;
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
}

function applyDocTitle(displayName) {
  if (!displayName) return;
  try {
    document.title = displayName;
  } catch (_e) {
    /* SSR / restricted envs — ignore */
  }
}

export function BrandingProvider({ children, activeClientId: clientIdOverride }) {
  // Build-time override (Feenix Partner Pilot has its own SPA bundle).
  const envClientId = process.env.REACT_APP_TENANT_ID || null;

  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Active client id flows env → explicit prop (rare, advanced cases) →
  // ``null`` (use default). AuthContext-driven flow can re-call
  // ``refreshBranding(clientId)`` after login.
  const [activeClientId, setActiveClientId] = useState(
    clientIdOverride || envClientId || null,
  );

  const refreshBranding = useCallback(async (clientId) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = clientId
        ? await brandingAPI.getForClient(clientId)
        : await brandingAPI.getDefault();
      const merged = { ...DEFAULT_BRANDING, ...(data || {}) };
      setBranding(merged);
      applyThemeVars(merged.theme);
      applyFavicon(merged.favicon_url);
      applyDocTitle(merged.display_name);
    } catch (e) {
      // Branding is non-critical — leave the in-bundle defaults in
      // place and surface the error for diagnostics only.
      setError(e?.response?.data?.detail || e?.message || 'branding fetch failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshBranding(activeClientId);
  }, [activeClientId, refreshBranding]);

  const setClientId = useCallback((clientId) => {
    setActiveClientId(clientId || null);
  }, []);

  const value = useMemo(
    () => ({
      branding,
      loading,
      error,
      activeClientId,
      setClientId,
      refreshBranding,
    }),
    [branding, loading, error, activeClientId, setClientId, refreshBranding],
  );

  return (
    <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>
  );
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    // Returning a sensible no-op default keeps unit tests / Storybook
    // stories that forget to wrap with the provider rendering instead
    // of throwing — branding is decoration, not load-bearing logic.
    return {
      branding: DEFAULT_BRANDING,
      loading: false,
      error: null,
      activeClientId: null,
      setClientId: () => {},
      refreshBranding: async () => {},
    };
  }
  return ctx;
}

export default BrandingContext;
